import { useEffect, useState } from 'react';

import { fetchUserRoleReason, revokeRoleToUser, useFetchGrantedRolesToUser } from '@jobandtalent/auth-api-client-v2';
import { Button } from '@jobandtalent/design-system';

import Privilege from 'src/components/Privilege/Privilege';
import Section from 'src/components/Section/Section';
import useError, { DEFAULT_ERROR } from 'src/hooks/useError/useError';
import useLocalPermissions from 'src/hooks/useLocalPermissions/useLocalPermissions';
import useNotification, { DEFAULT_SUCCESS } from 'src/hooks/useNotification/useNotification';

import NewRoleForm from '../NewRoleForm/NewRoleForm';
import { UserRoleAssignment } from '../Users.types';
import { useUserState } from '../UserState';

import styles from './EditUserForm.module.scss';

const UserRolesSection = () => {
  const { state } = useUserState();
  const userId = state.user?.id;
  const { error, setError } = useError();
  const { notification, setNotification } = useNotification();
  const [showNewRoleForm, setShowNewRoleForm] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRoleAssignment | null>();
  const { permissions, error: permissionError } = useLocalPermissions();

  const {
    data: userRolesData,
    error: userRolesError,
    mutate: userRolesMutate
  } = useFetchGrantedRolesToUser(state.user?.id as number);

  const onClickRevokeRole = async (roleId: number) => {
    if (!state.user) {
      return;
    }

    revokeRoleToUser(state.user.id, roleId).then(
      () => userRolesMutate(),
      (error) => {
        setError(error?.response?.data ? error.response.data : DEFAULT_ERROR);
      }
    );
  };

  const onCloseNewRoleForm = () => {
    setBulkMode(false);
    setShowNewRoleForm(false);
    setSelectedRole(null);
  };

  const onSuccessNewRoleForm = () => {
    userRolesMutate();
    setNotification(DEFAULT_SUCCESS);
    setShowNewRoleForm(false);
    setSelectedRole(null);
  };

  useEffect(() => {
    if (userRolesError) {
      setError(userRolesError);
    }
  }, [userRolesError]);

  const companyAdminPermission = !permissionError && permissions.companyAdminPermission;
  const serviceUser = state.user?.service_user
  const ServiceUsersPermissions = !permissionError && permissions.serviceUsersPermission;
  const canViewLiteWebappRoles =
    !permissionError &&
    (permissions.liteWebappRolesPermission || permissions.manageLiteWebappUsersPermission);
  const userRoles = userRolesData?.data.user_roles || [];
  const liteWebappRoles = userRoles.filter((role) => role.category === 'lite_webapp');
  const standardRoles = userRoles.filter((role) => role.category !== 'lite_webapp');

  const renderRole = (role: UserRoleAssignment) => (
    <Privilege
      key={role.id}
      onRevoke={(!companyAdminPermission && role.category === 'company_app') || (serviceUser && !ServiceUsersPermissions) ? undefined : () => onClickRevokeRole(role.role_id)}
      onEdit={(!companyAdminPermission && role.category === 'company_app') || (serviceUser && !ServiceUsersPermissions) ? undefined : () => {
        setBulkMode(false);
        setShowNewRoleForm(true);
        setSelectedRole(role);
      }}
      title={role.role_name}
      canReadReason={!permissionError && permissions.readAssignmentDetailsPermission}
      loadReason={userId !== undefined ? () => fetchUserRoleReason(userId, role.role_id) : undefined}
      subtitles={[
        role.role_scope_name || '-',
        datascopeValue(role),
        role.category === 'company_app' ? 'Company App' : ''
      ]}
    />
  );

  return (
    <>
      <Section icon="person_accepted" title="Roles">
        {standardRoles.map(renderRole)}
        {canViewLiteWebappRoles && liteWebappRoles.length > 0 && (
          <div className={styles.liteWebappRoles}>
            <div className={styles.liteWebappRolesTitle}>Lite Webapp Roles</div>
            {liteWebappRoles.map(renderRole)}
          </div>
        )}
        <div className={styles.grantActions}>
          <Button
            disabled={(serviceUser && !ServiceUsersPermissions)}
            className={styles.grantPrivilege}
            dataAttributes={{
              testid: 'grantRoleButton'
            }}
            onSelect={() => {
              setBulkMode(false);
              setShowNewRoleForm(true);
            }}
            secondary
            tiny
          >
            Grant role
          </Button>
          <Button
            disabled={(serviceUser && !ServiceUsersPermissions)}
            className={styles.grantPrivilege}
            onSelect={() => {
              setBulkMode(true);
              setShowNewRoleForm(true);
            }}
            secondary
            tiny
          >
            Bulk add
          </Button>
        </div>
      </Section>
      {showNewRoleForm && state.user && (
        <NewRoleForm
          bulk={bulkMode}
          onAssignmentsChanged={() => userRolesMutate()}
          onClose={onCloseNewRoleForm}
          onSuccess={onSuccessNewRoleForm}
          selectedUser={state.user}
          selectedRole={selectedRole}
        />
      )}
      {error}
      {notification}
    </>
  );
};

const datascopeValue = (role: UserRoleAssignment): string => {
  if(role.data_scope && role.data_scope.length > 0) {
    return `Data scopes: ${role.data_scope.join(", ")}`;
  }

  return "";
}

export default UserRolesSection;
